import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import TopBar from "../components/TopBar";
import { ReactComponent as BackIcon } from "../assets/icon_back.svg";
import Colors from "../constants/Colors";
import { msToDateInput, dateInputToMs } from "../utils/invoiceDates";

const DANGER = "#e5484d";

function InvoiceFormScreen() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { invoices, clients, projects, updateInvoice, deleteInvoice } = useContext(FirebaseContext);

  const invoice = invoices.find((i) => String(i.id) === String(invoiceId)) || null;
  const [draft, setDraft] = useState(invoice ? { ...invoice } : null);
  const dirtyRef = useRef(false);

  // Seed the editor; re-seed from live data only while the user isn't editing
  // (so a remote change reflects without clobbering in-progress edits).
  useEffect(() => {
    if (invoice && (!draft || (!dirtyRef.current && invoice.updatedAt !== draft.updatedAt))) {
      setDraft({ ...invoice });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice && invoice.updatedAt]);

  const persist = (patch) => {
    if (!invoice) return;
    updateInvoice(invoice.id, patch);
  };

  // Debounced autosave of the draft (1s after the last change).
  useEffect(() => {
    if (!draft || !dirtyRef.current) return;
    const t = setTimeout(() => {
      persist({
        number: Number(draft.number) || 0,
        clientId: Number(draft.clientId) || 0,
        projectIds: (draft.projectIds || []).map(Number),
        periodStart: draft.periodStart || 0,
        periodEnd: draft.periodEnd || 0,
        issueDate: draft.issueDate || 0,
        dueDate: draft.dueDate || 0,
        taxRatePercent: Number(draft.taxRatePercent) || 0,
        notes: draft.notes || "",
      });
      dirtyRef.current = false;
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const set = (patch) => {
    dirtyRef.current = true;
    setDraft((d) => ({ ...d, ...patch }));
  };

  const toggleProject = (pid) => {
    const ids = (draft.projectIds || []).map(Number);
    const next = ids.includes(Number(pid)) ? ids.filter((x) => x !== Number(pid)) : [...ids, Number(pid)];
    set({ projectIds: next });
  };

  const flushThen = (fn) => {
    if (!invoice) return;
    // Persist any pending edit immediately, then run the navigation.
    persist({
      number: Number(draft.number) || 0,
      clientId: Number(draft.clientId) || 0,
      projectIds: (draft.projectIds || []).map(Number),
      periodStart: draft.periodStart || 0,
      periodEnd: draft.periodEnd || 0,
      issueDate: draft.issueDate || 0,
      dueDate: draft.dueDate || 0,
      taxRatePercent: Number(draft.taxRatePercent) || 0,
      notes: draft.notes || "",
    });
    dirtyRef.current = false;
    fn();
  };

  const handleDelete = () => {
    if (!invoice) return;
    if (window.confirm("Delete this invoice?")) {
      deleteInvoice(invoice.id);
      navigate("/invoice");
    }
  };

  if (!draft) {
    return (
      <div style={styles.container}>
        <TopBar
          leftContainer={
            <button onClick={() => navigate("/invoice")} style={styles.backBtn} aria-label="Back">
              <BackIcon style={{ width: 22, height: 22, fill: "var(--text)" }} />
            </button>
          }
          centerContainer={<span style={styles.headerTitle}>Invoice</span>}
        />
        <div style={styles.body}>
          <div style={{ color: "var(--text-muted)" }}>Loading invoice…</div>
        </div>
      </div>
    );
  }

  const selectedProjectIds = new Set((draft.projectIds || []).map(Number));

  return (
    <div style={styles.container}>
      <TopBar
        leftContainer={
          <button onClick={() => navigate("/invoice")} style={styles.backBtn} aria-label="Back">
            <BackIcon style={{ width: 22, height: 22, fill: "var(--text)" }} />
          </button>
        }
        centerContainer={<span style={styles.headerTitle}>Invoice #{draft.number}</span>}
      />
      <div style={styles.body}>
        <label style={styles.label}>Invoice number</label>
        <input
          style={styles.input}
          type="text"
          inputMode="numeric"
          value={draft.number}
          onChange={(e) => set({ number: e.target.value.replace(/[^0-9]/g, "") })}
        />

        <label style={styles.label}>Client</label>
        <select
          style={styles.input}
          value={String(draft.clientId || 0)}
          onChange={(e) => set({ clientId: Number(e.target.value) })}
        >
          <option value="0">No client</option>
          {clients.map((c) => (
            <option key={c.id} value={Number(c.id)}>
              {c.name || "(no name)"}
            </option>
          ))}
        </select>

        <label style={styles.label}>Projects to bill</label>
        <div style={styles.chipRow}>
          {projects
            .filter((p) => !p.projectArchived)
            .map((p) => (
              <button
                key={p.id}
                style={selectedProjectIds.has(Number(p.id)) ? styles.chipOn : styles.chip}
                onClick={() => toggleProject(p.id)}
              >
                {p.projectName || "Untitled"}
              </button>
            ))}
          {projects.length === 0 && <span style={styles.muted}>No projects.</span>}
        </div>

        <div style={styles.dateGrid}>
          <div>
            <label style={styles.label}>Period start</label>
            <input style={styles.input} type="date" value={msToDateInput(draft.periodStart)} onChange={(e) => set({ periodStart: dateInputToMs(e.target.value) })} />
          </div>
          <div>
            <label style={styles.label}>Period end</label>
            <input style={styles.input} type="date" value={msToDateInput(draft.periodEnd)} onChange={(e) => set({ periodEnd: dateInputToMs(e.target.value) })} />
          </div>
          <div>
            <label style={styles.label}>Issue date</label>
            <input style={styles.input} type="date" value={msToDateInput(draft.issueDate)} onChange={(e) => set({ issueDate: dateInputToMs(e.target.value) })} />
          </div>
          <div>
            <label style={styles.label}>Due date</label>
            <input style={styles.input} type="date" value={msToDateInput(draft.dueDate)} onChange={(e) => set({ dueDate: dateInputToMs(e.target.value) })} />
          </div>
        </div>

        <label style={styles.label}>Tax rate (%)</label>
        <input
          style={styles.input}
          type="text"
          inputMode="decimal"
          value={draft.taxRatePercent}
          onChange={(e) => set({ taxRatePercent: e.target.value.replace(/[^0-9.]/g, "") })}
        />

        <label style={styles.label}>Notes</label>
        <textarea
          style={{ ...styles.input, minHeight: "80px", resize: "vertical" }}
          value={draft.notes || ""}
          onChange={(e) => set({ notes: e.target.value })}
        />

        <div style={{ height: "16px" }} />
        <button style={styles.deleteBtn} onClick={handleDelete}>
          Delete invoice
        </button>
      </div>

      <div style={styles.ctaBar}>
        <button style={styles.primaryCta} onClick={() => flushThen(() => navigate(`/invoicePreview/${draft.id}`))}>
          Preview &amp; PDF
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: { paddingTop: "50px", height: "calc(100vh - 60px)", boxSizing: "border-box" },
  backBtn: { background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", padding: 0 },
  headerTitle: { fontWeight: 700 },
  body: { padding: "16px", paddingBottom: "90px", maxWidth: "560px", margin: "0 auto", height: "100%", overflowY: "auto", boxSizing: "border-box" },
  label: { fontSize: "12px", color: "var(--text-muted)", margin: "12px 0 4px", display: "block" },
  input: { padding: "10px", borderRadius: "8px", border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: "14px", width: "100%", boxSizing: "border-box" },
  chipRow: { display: "flex", gap: "8px", flexWrap: "wrap" },
  chip: { background: "transparent", border: "1px solid var(--border)", color: "var(--text)", borderRadius: "999px", padding: "6px 14px", fontSize: "13px", cursor: "pointer" },
  chipOn: { background: Colors.primary, border: `1px solid ${Colors.primary}`, color: "#fff", borderRadius: "999px", padding: "6px 14px", fontSize: "13px", cursor: "pointer" },
  muted: { color: "var(--text-muted)", fontSize: "13px" },
  dateGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" },
  deleteBtn: { background: "none", border: "none", color: DANGER, fontWeight: 700, cursor: "pointer", padding: "8px 0" },
  ctaBar: { position: "fixed", left: 0, right: 0, bottom: "60px", padding: "12px 16px", background: "var(--bg)", borderTop: "1px solid var(--border)", boxSizing: "border-box" },
  primaryCta: { width: "100%", padding: "12px", background: Colors.primary, color: "#fff", border: "none", borderRadius: "10px", fontWeight: 700, cursor: "pointer" },
};

export default InvoiceFormScreen;
